import {
    ApiQueryEndpoints,
    FilterMetadata,
    QueryResponse,
} from "~/modules/filters/types";
import { ExportTableNames } from "~/modules/downloads/types";
import {
    ApiQueryFetcher,
    ApiQueryStateManager,
    ApiSearchLayout,
} from "~/modules/filters/components/ApiSearchLayout";
import { SearchControlModes } from "~/modules/filters/components/FilterControls/types";
import { DEFAULT_ASSET_LIST_QUERY } from "../../consts";
import { Asset } from "../../types";
import { DataGrid, RowSizes } from "~/components/DataGrid";
import { NETWORKS_COLUMN } from "../../components/AssetTable/columns/Networks";
import { HOLDING_ADDRESS_COUNT_COLUMN } from "../../components/AssetTable/columns/Holders";
import { AssetTableRow } from "../../components/AssetTable/AssetTableRow";
import { ASSET_CLASS_COLUMN } from "../../components/AssetTable/columns/AssetClass";
import { ASSET_NAME_AND_TICKER_COLUMN_EXPANDABLE } from "../../components/AssetTable/columns/AssetName";
import { BRIDGED_TOKEN_VALUE_DOLLAR_COLUMN } from "../../components/AssetTable/columns/BridgedTokenValueDollar";
import { CONTRACT_ADDRESS_COLUMN } from "../../components/AssetTable/columns/ContractAddress";
import { INCORPORATION_COUNTRY_COLUMN } from "../../components/AssetTable/columns/IncorporationCountry";
import { MANAGEMENT_FEE_COLUMN } from "../../components/AssetTable/columns/ManagementFee";
import { PRIMARY_MARKET_MINIMUM_SUBSCRIPTION_AMT_COLUMN } from "../../components/AssetTable/columns/PrimaryMarket";
import { LICENSE_OR_REGULATORY_FRAMEWORK } from "../../components/AssetTable/columns/RegulatoryFramework";
import { SECTORS_COLUMN } from "../../components/AssetTable/columns/Sectors";
import { TOTAL_PERFORMANCE_FEE_COLUMN } from "../../components/AssetTable/columns/TotalPerformanceFee";

// // // //

const TABLE_COLUMNS = [
    ASSET_NAME_AND_TICKER_COLUMN_EXPANDABLE,
    NETWORKS_COLUMN,
    BRIDGED_TOKEN_VALUE_DOLLAR_COLUMN,
    // NET_ASSET_VALUE_COLUMN,
    HOLDING_ADDRESS_COUNT_COLUMN,
    ASSET_CLASS_COLUMN,
    SECTORS_COLUMN,
    // MANAGER_COLUMN,
    INCORPORATION_COUNTRY_COLUMN,
    LICENSE_OR_REGULATORY_FRAMEWORK,
    PRIMARY_MARKET_MINIMUM_SUBSCRIPTION_AMT_COLUMN,
    MANAGEMENT_FEE_COLUMN,
    TOTAL_PERFORMANCE_FEE_COLUMN,
    CONTRACT_ADDRESS_COLUMN,
];

export function AssetListing(props: {
    initialState: QueryResponse<Asset>;
    filterMetadata?: FilterMetadata[];
}) {
    const { initialState, filterMetadata = [] } = props;

    return (
        <ApiQueryStateManager
            // syncUrl
            defaultFilters={DEFAULT_ASSET_LIST_QUERY}
        >
            {({ appliedQuery, internalQuery, resetQuery }) => (
                <ApiQueryFetcher
                    appliedQuery={appliedQuery.query}
                    internalQuery={internalQuery.query}
                    endpoint={ApiQueryEndpoints.AssetV4}
                    initialState={initialState}
                >
                    {(state) => {
                        return (
                            <ApiSearchLayout
                                filterMetadata={filterMetadata}
                                onResetQuery={() => {
                                    resetQuery();
                                    // TODO - reset query state here, from ApiQueryStateManager
                                }}
                                // NOTE - the `export` feature needs a mechanism to define the filename for each layout
                                // i.e. any asset/asset-summary exports will end up having the same filename without
                                appliedQuery={appliedQuery}
                                internalQuery={internalQuery}
                                queryFetcherState={state}
                                controls={{
                                    exportData: {
                                        enabled: true,
                                        exportTableName:
                                            ExportTableNames.assets_v4,
                                    },
                                    search: {
                                        enableBasic: false,
                                        enablePro: true,
                                        mode: SearchControlModes.Basic,
                                    },
                                    apiTools: {
                                        enabled: true,
                                        endpoint: ApiQueryEndpoints.AssetV4,
                                    },
                                    queryExamples: {
                                        items: [],
                                        onSelectItem: (selectedExample) => {
                                            internalQuery.update(
                                                selectedExample.query,
                                            );
                                        },
                                    },
                                    sharing: {
                                        enabled: true,
                                        url: appliedQuery.url,
                                    },
                                }}
                            >
                                <DataGrid
                                    rowKey="id"
                                    analyticsTableName={"assets"}
                                    columns={TABLE_COLUMNS}
                                    rows={state.appliedQueryReq.data.results}
                                    query={appliedQuery.query}
                                    onChangeQuery={(updatedQuery) => {
                                        appliedQuery.update(updatedQuery);
                                    }}
                                    bordered
                                    size={RowSizes.small}
                                    loading={false}
                                    fixedHeader
                                    paginationState={
                                        state.appliedQueryReq.data.pagination
                                    }
                                    enableScrollButton={false}
                                    flush={false}
                                    showHeader={true}
                                >
                                    {({ row, rowIndex, scrollLeft }) => (
                                        <AssetTableRow
                                            asset={row}
                                            key={row.id}
                                            columns={TABLE_COLUMNS}
                                            size={RowSizes.small}
                                            bordered
                                            scrollLeft={scrollLeft}
                                            striped={rowIndex % 2 === 1}
                                            rowIndex={rowIndex}
                                            defaultExpandedState={false}
                                        />
                                    )}
                                </DataGrid>
                            </ApiSearchLayout>
                        );
                    }}
                </ApiQueryFetcher>
            )}
        </ApiQueryStateManager>
    );
}
